
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import { loginStart, loginSuccess, loginFailure } from '@store/slices/authSlice'
import { selectAuthLoading, selectAuthError } from '@store/slices/authSlice'

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional()
})
console.log("apiUrl", import.meta.env.VITE_API_BASE_URL);
const Login = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  
  const isLoading = useSelector(selectAuthLoading)
  const error = useSelector(selectAuthError)
  

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: 'admin@example.com',
      password: 'finalpass123',
      rememberMe: false
    }
  })

  const onSubmit = async (data) => {
    try {
      dispatch(loginStart())
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL
      const response = await fetch(`${apiUrl}/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (result.success) {
        dispatch(loginSuccess({
          token: result.accessToken,
          user: result.user
        }))
        navigate('/dashboard')
      } else {
        dispatch(loginFailure(result.message || 'Login failed'))
      }
    } catch (err) {
      console.error('Login error:', err)
      dispatch(loginFailure('Network error. Please try again.'))
    }
  }



  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card
          sx={{
            width: { xs: '100%', sm: 400 },
            maxWidth: '100%',
            boxShadow: theme.shadows[8]
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Typography
                  variant="h4"
                  component="h1"
                  color="primary"
                  fontWeight="bold"
                  gutterBottom
                >
                  Smart Reminder
                </Typography>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t('auth.welcome')}
                </Typography>
              </motion.div>
            </Box>

            {/* Error Alert */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              </motion.div>
            )}

            {/* Login Form */}
            <motion.form
              onSubmit={handleSubmit(onSubmit)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <TextField
                {...register('email')}
                fullWidth
                label={t('auth.email')}
                type="email"
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={isLoading}
                sx={{ mb: 2 }}
              />

              <TextField
                {...register('password')}
                fullWidth
                label={t('auth.password')}
                type="password"
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={isLoading}
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    {...register('rememberMe')}
                    disabled={isLoading}
                  />
                }
                label={t('auth.rememberMe')}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ mb: 2, py: 1.5 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('auth.login')
                )}
              </Button>
            </motion.form>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  )
}

export default Login
